import React, { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import type { FormProps } from 'react-final-form'
import { Form } from 'react-final-form'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { Pagination } from '@frontend/pole-ui/lib/components/Pagination'
import classNames from 'classnames'
import type { FormApi } from 'final-form'
import find from 'lodash/find'
import isEmpty from 'lodash/isEmpty'
import omit from 'lodash/omit'
import omitBy from 'lodash/omitBy'
import { OwnOfferButton } from '@/components/composed/harvestSaleLots'
import { openModal } from '@/components/composed/Modal/actions'
import { addModalToWellKnown } from '@/components/composed/Modal/constants'
import { Breadcrumbs, Loader, MetaInfo, PageNoticeText } from '@/components/ui'
import { getPageNumberInQuery } from '@/logic/app/reducer'
import { getAuthState, isAuthenticated as isUserAuthenticated, isImpersonationUser, isUserAgrarian } from '@/logic/auth/reducer'
import { TemplatesFilterName } from '@/logic/harvestSale/constants'
import asyncEpics from '@/logic/harvestSale/epics'
import {
  getFilters,
  getIndicators,
  getPagesQuantity,
  getPaginationInfo,
  getTemplates,
  isFetchingTemplatesFailed,
  isTemplatesFetching,
} from '@/logic/harvestSale/reducer'
import { pushBtnBannerHarvestGtmEvent, pushHarvestGtmEvent } from '@/logic/metrika/harvest/gtmEvents'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { HarvestSaleLotsAccessControlProvider } from '@/pages/HarvestSaleLots/components/HarvestSaleLotsAccessControlProvider'
import type { RootState } from '@/redux/interfaces'
import { EHarvestSaleRoute } from '@/types/EHarvestSaleRoute'
import { addNewEpicsLazily } from '@/utils/addNewEpicsLazily'
import { getWordInDativeCase } from '@/utils/declination'
import { formatLocalizedString } from '@/utils/formatUtils'
import { useDeviceType } from '@/utils/hooks/useDeviceType'
import { useHarvestSaleLotsFormApi } from '@/utils/outputs/useHarvestSaleLotsFormApi'
import { useHarvestSaleLotsPageParams } from '@/utils/outputs/useHarvestSaleLotsPageParams'
import type { IRequestHarvestSaleByTemplateModalOptions } from './components/RequestHarvestSaleByTemplateModal/interfaces'
import { TopFilterDesktop } from './components/TopFilter/components'
import { DropdownFilters, LeftFilter, NoTemplatesNotice, RequestHarvestSaleByTemplateModal, Templates } from './components'
import { excludedFilterNames, harvestSaleBreadcrumbs, HarvestSaleLotsContext, templatesPerPage } from './constants'
import type { IHarvestSaleLotsContext } from './interfaces'
import { messages } from './messages'
import './styles.scss'

addModalToWellKnown({ RequestHarvestSaleByTemplateModal })

addNewEpicsLazily([asyncEpics])

const mapState = (state: RootState) => ({
  isFetching: isTemplatesFetching()(state),
  indicators: getIndicators()(state),
  templates: getTemplates()(state),
  pagesQuantity: getPagesQuantity(state),
  isFailure: isFetchingTemplatesFailed()(state),
  pageNumber: getPageNumberInQuery(state),
  topFilters: getFilters()(state).top,
  paginationInfo: getPaginationInfo()(state),
  allFilters: getFilters()(state).all,
  isAgrarian: isUserAgrarian()(state),
  isAuthenticated: isUserAuthenticated()(state),
  isImpersonation: isImpersonationUser()(state),
  userGtmInfo: getUserGtmInfo()(state),
  auth: getAuthState()(state),
})

const HarvestSaleLots: React.FC = () => {
  const dispatch = useDispatch()
  const { cultureId } = useHarvestSaleLotsPageParams()

  const {
    templates = [],
    pagesQuantity,
    pageNumber,
    isFetching,
    isFailure,
    topFilters,
    allFilters,
    indicators,
    isAgrarian,
    isAuthenticated,
    isImpersonation,
    userGtmInfo,
    auth,
  } = useSelector(mapState)

  const { userRegion } = useUserRegion()

  const { onChangePage, submitFilterForm, resetFilterForm, saveFilterValues, savedFilterValues, cultures } =
    useHarvestSaleLotsFormApi(templatesPerPage)

  const { isDesktop, isMobile } = useDeviceType()

  const [paginationKey, setPaginationKey] = useState<number>(0)
  const [dropdownKey, setDropdownKey] = useReducer(prevState => prevState + 1, 0)

  const { harvestSaleLotsBreadcrumbs, cultureName } = useMemo(() => {
    const { name: foundCultureName, value = '' } = find(find(allFilters, { value: 'cultures' })?.options, { value: cultureId }) || {}

    return {
      harvestSaleLotsBreadcrumbs: [
        ...harvestSaleBreadcrumbs,
        {
          caption: foundCultureName,
          link:
            isAgrarian || !isAuthenticated || isImpersonation
              ? `${EHarvestSaleRoute.main}/${value}`
              : `${EHarvestSaleRoute.main}/${value}/lots`,
        },
        { caption: messages.pageBreadcrumb },
      ],
      cultureName: foundCultureName,
    }
  }, [allFilters, cultureId, isAgrarian, isAuthenticated, isImpersonation])

  const accessControlContext = useMemo(
    () => ({
      auth,
    }),
    [auth],
  )

  const pushGtmEvent = useCallback(() => {
    pushBtnBannerHarvestGtmEvent({
      items: [{ name: 'applicationBanner', category: cultureId }],
      pagePart: 'content',
      blockName: 'blockListingOutputs',
      eventCategory: 'ecommerce',
      eventAction: 'click',
      eventLabel: 'buttonCreateApplication',
      eventStatus: 'success',
      ecommerceType: 'add',
      eventEcommerce: 'addToCart',
      eventNonInteraction: '0',
      trueEventContext: 'lst:listingLotsOutputs',
      actionField: {
        list: 'listingLotsOutputs',
      },
    })
  }, [cultureId])

  const pushButtonHarvestGtmEvent = () => {
    pushBtnBannerHarvestGtmEvent({
      items: [{ name: 'applicationButton', category: cultureId }],
      pagePart: 'content',
      blockName: 'blockListingOutputs',
      eventCategory: 'ecommerce',
      eventAction: 'click',
      eventLabel: 'buttonCreateApplication',
      eventStatus: 'success',
      ecommerceType: 'add',
      eventEcommerce: 'addToCart',
      eventNonInteraction: '0',
      trueEventContext: 'lst:listingLotsOutputs',
      actionField: {
        list: 'listingLotsOutputs',
      },
    })
  }

  useEffect(() => {
    if (templates.length) {
      pushHarvestGtmEvent(
        {
          items: templates,
          eventAction: 'viewing',
          eventLabel: 'lotsOutputs',
          eventStatus: 'success',
          blockName: 'blockListingOutputs',
          eventCategory: 'ecommerce',
          ecommerceType: 'impressions',
          eventEcommerce: 'impressions',
          eventNonInteraction: '0',
          pagePart: 'content',
          itemContext: 'listingLotsOutputs',
        },
        userRegion.name,
        userGtmInfo,
      )
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(templates)])

  const openRequestingHarvestSaleModal = (contentOptions: IRequestHarvestSaleByTemplateModalOptions) =>
    dispatch(
      openModal({
        options: {
          dialogId: 'RequestHarvestSaleByTemplateModal',
        },
        contentOptions,
      }),
    )

  if (isFailure) return <PageNoticeText>{messages.error}</PageNoticeText>

  return (
    <HarvestSaleLotsAccessControlProvider context={accessControlContext}>
      <section className="page-wrapper page-wrapper_no-mobile page-wrapper_no-tablet">
        <MetaInfo metaInfo={{ title: cultureName }} />
        <div className="page-wrapper_tablet page-wrapper_mobile">
          {<Breadcrumbs isMobile={isMobile} customBreadcrumbs={harvestSaleLotsBreadcrumbs} />}
          {cultureName && (
            <div
              className="row row-align_base row-justify_between space-holder48-from-md
        space-holder32-only-md space-holder16-sm space-holder-top24-from-sm"
            >
              <h3>{formatLocalizedString(messages.pageTitle, { culture: getWordInDativeCase(cultureName.toLowerCase()) })}</h3>
              <OwnOfferButton onButtonClick={pushButtonHarvestGtmEvent} />
            </div>
          )}
        </div>
        <div className="harvest-sale-page">
          <Loader isVisible={isFetching} />

          <Form onSubmit={submitFilterForm} initialValues={savedFilterValues}>
            {({ form, values, initialValues }: FormProps) => {
              const onChangeFilter = () =>
                setTimeout(() => {
                  setPaginationKey(paginationKey + 1)
                  // eslint-disable-next-line padding-line-between-statements
                  ;(form as FormApi).submit()
                }, 100)

              const cultureValues = values[TemplatesFilterName.CULTURES]

              const selectedCultureId = Array.isArray(cultureValues)
                ? cultureValues.toString()
                : Object.keys(omitBy(cultureValues, value => !value))?.toString()

              const countOfSelectedFilters: number = Object.entries(values).reduce((acc, [key, value]) => {
                const selectedInArray = Array.isArray(value) && value.length
                const selectedInObject = typeof value === 'object' && value !== null && Object.values(value).find(Boolean)
                if (!excludedFilterNames.includes(key) && (selectedInArray || selectedInObject)) return acc + 1

                return acc
              }, 0)

              const context: IHarvestSaleLotsContext = {
                resetFilter: () => {
                  setDropdownKey()
                  form?.reset(initialValues)
                  resetFilterForm()
                },
                onChangeFilter,
                selectedCultureId,
                isFilterChosen: !isEmpty(omit(values, ['store'])),
                initialValues,
                dropdownKey,
                setDropdownKey,
                formValues: values,
                onChangeFormValue: form?.change,
                topFilters,
                allFilters,
                saveFilterValues,
                savedFilterValues,
                countOfSelectedFilters,
                openRequestingHarvestSaleModal,
                cultures,
              }

              return (
                <HarvestSaleLotsContext.Provider value={context}>
                  <div className="aside-and-content-wrapper">
                    {isDesktop && (
                      <aside className="aside-and-content-wrapper__aside">
                        <LeftFilter />
                      </aside>
                    )}
                    <div className="aside-and-content-wrapper__content">
                      <TopFilterDesktop />
                      <DropdownFilters
                        className={classNames('harvest-sale-page__dropdown-filters', {
                          'harvest-sale-page__dropdown-filters__solo': !indicators.length,
                        })}
                      />
                      {templates.length ? (
                        <>
                          <Templates
                            templates={templates}
                            onBannerButtonClick={pushGtmEvent}
                            openRequestingHarvestSaleModal={openRequestingHarvestSaleModal}
                          />
                          <div className="harvest-sale-pagination page-wrapper">
                            <Pagination hidePrevButton onChange={onChangePage} page={pageNumber} count={pagesQuantity} />
                          </div>
                        </>
                      ) : (
                        <NoTemplatesNotice isAuthenticated={isAuthenticated} isAgrarian={isAgrarian} />
                      )}
                    </div>
                  </div>
                </HarvestSaleLotsContext.Provider>
              )
            }}
          </Form>
        </div>
      </section>
    </HarvestSaleLotsAccessControlProvider>
  )
}

export default HarvestSaleLots
